import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/profile';

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/profile';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-ivory" data-testid="login-page">
      <div className="max-w-md w-full space-y-10">
        <div className="text-center space-y-4">
          <p className="label-editorial">Welcome Back</p>
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal" data-testid="login-title">
            Enter the <em className="text-burgundy not-italic">sisterhood</em>.
          </h1>
        </div>

        <button
          onClick={handleGoogleLogin}
          className="w-full py-4 border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory transition-colors font-medium text-sm uppercase tracking-widest flex items-center justify-center gap-3"
          data-testid="google-login-button"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity="0.6"/>
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4">
          <div className="flex-1 hairline"></div>
          <span className="text-xs text-muted-warm font-mono uppercase tracking-widest">or</span>
          <div className="flex-1 hairline"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          <div>
            <label htmlFor="email" className="label-editorial block mb-3">Email</label>
            <input
              id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
              data-testid="email-input"
            />
          </div>
          <div>
            <label htmlFor="password" className="label-editorial block mb-3">Password</label>
            <input
              id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
              data-testid="password-input"
            />
          </div>
          <button
            type="submit" disabled={loading}
            className="btn-burgundy w-full py-4 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="login-submit-button"
          >
            {loading ? 'Logging in' : 'Log In'} <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        <p className="text-center text-charcoal-muted text-sm">
          Don't have an account?{' '}
          <Link to="/register" className="text-burgundy hover:underline font-medium" data-testid="register-link">
            Join the sisterhood
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
